import { NumberQuizState } from "@shared/types/NumberQuizState";
import { NumberQuizService } from "../services/NumberQuizService";
import { css, html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { Page } from "../enums/Page";

const styles: string = css`
    * {
        font-family: "Onesize";
    }
    .maya-number {
        font-family: "Noto Sans Mayan Numerals";
    }
    .text-center {
        text-align: center;
    }
    .container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
    .puzzle {
        background: #617667;
        padding: 1.5rem 1.5rem;
        border-radius: 18px;
    }
    .number{
        text-align: center;
        font-size: 3rem;
        display: flex;
        flex-direction: column;
        line-height: 3rem;
    }
    .answers {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: .5rem;
        margin: 20px 0;
    }
    .answer-btn {
        padding: 1rem;
        font-size: 1.5rem;
        cursor: pointer;
    }
    .feedback {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        margin: 10px 0;
        padding: 10px;
        border-radius: 5px;
    }
    .feedback-correct {
        color: #fff;
        background-color: #4CAF50;
    }
    .feedback-wrong {
        color: #fff;
        background-color: #f44336;
    }
    #closeGame {
        font-size: 1.25rem;
        background: #617667;
        padding: 1rem 2rem;
        color: white;
        cursor: pointer;
        border-radius: 18px;
    }
    #closeGame:hover {
        background: #ae5852;
    }
    table.numbers {
        width: 50%;
        margin: auto;
    }
    td{
        border: 1px solid #dddddd;
        text-align: left;
        padding: 8px;
    }
    tr td:nth-child(2) {
        padding: 20px;
    }
`;

export class NumberQuizComponent extends HTMLElement {
    private _numberQuizService: NumberQuizService = new NumberQuizService();
    private readonly _gameEventService: GameEventService = new GameEventService();

    /**
     * Constructor for NumberQuizComponent
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
    }

    private async refreshGameState(): Promise<void> {
        const quizState: NumberQuizState = await this._numberQuizService.getQuestion();
        this.render(quizState);
    }

    private render(state: NumberQuizState): void {
        if (!this.shadowRoot) return;

        const element: HTMLElement = html`
            <div class="container">
                <div class="puzzle">
                    <div id= "qFeedback"></div>
                    <h1 class="text-center">Lives: ${state.lives}</h1>
                    <h3 class="text-center">${state.title}</h3>
                    <table class="numbers">
                        <tr>
                            <td>20x</td>
                            <td class="maya-number">${state.number[1] ? state.number[1] : "𝋠"}</td>
                        </tr>
                        <tr>
                            <td>1x</td>
                            <td class="maya-number">${state.number[1] ? state.number[0] : state.number[0]}</td>
                        </tr>
                    </table>
                    <div class="answers">
                        ${state.answers.map(answer => `<button class="answer-btn">${answer}</button>`).join("")}
                    </div>
                </div> 
                <button id="closeGame">X</button>
            </div>
        `;
        const buttons: NodeListOf<HTMLButtonElement> = element.querySelectorAll(".answer-btn");
        const btn: HTMLButtonElement = element.querySelector("#closeGame")!;

        btn.addEventListener("click", () => this._gameEventService.switchPage(Page.Canvas));

        buttons.forEach((button, index) => button.addEventListener("click", async () => {
            const state: NumberQuizState = await this._numberQuizService.checkAnswer(index);
            const feedback: HTMLElement = element.querySelector("#qFeedback") as HTMLElement;

            if (state.solved) this._gameEventService.switchPage(Page.Canvas);

            if (state.correct) {
                feedback.className = "feedback feedback-correct";
                feedback.innerHTML = "<h3>Correct!</h3>";
            }
            else {
                feedback.className = "feedback feedback-wrong";
                feedback.innerHTML = "<h3>Wrong!</h3>";
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            setTimeout(() => feedback.className = "", 1000);
            feedback.className = "";
            feedback.innerHTML = "";
            await this.refreshGameState();
        }
        ));

        this.shadowRoot.replaceChildren(
            html`<style>${styles}</style>`,
            element
        );
    }
}
